import { useEffect, useState } from "react";
import { TrendingUp, TrendingDown, Coins, Loader2, Minus } from "lucide-react";
import api from "../../lib/api";

/**
 * Juni-2026 (User-Wunsch) — „Mein Verdienst diesen Monat".
 *
 * Zeigt dem Vermittler & Verkäufer seinen Verdienst des laufenden Monats
 * GETRENNT nach EUR-Provision (als Verkäufer/Mitarbeiter im Dienst) und
 * €Punkte (gesammelte Aktivitäts-Punkte). Inkl. Monatsvergleich
 * („+X % vs. Vormonat") zur Gamification. Quelle: GET /api/vermittler/earnings/monthly.
 */
const fmtEur = (n) =>
    Number(n || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";

/** Monatsvergleichs-Badge: grün bei Plus, rot bei Minus, neutral bei 0,
 * „Neu" wenn es im Vormonat keinen Vergleichswert gab (delta === null). */
function DeltaBadge({ delta, hasValue, testid }) {
    if (delta === null || delta === undefined) {
        if (!hasValue) return null;
        return (
            <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-sky-300" data-testid={testid}>
                <Sparkle /> Neu diesen Monat
            </span>
        );
    }
    const up = delta > 0;
    const flat = delta === 0;
    const Icon = flat ? Minus : up ? TrendingUp : TrendingDown;
    const cls = flat ? "text-gray-400" : up ? "text-emerald-400" : "text-red-400";
    return (
        <span className={`inline-flex items-center gap-0.5 text-[10px] font-bold ${cls}`} data-testid={testid}>
            <Icon className="w-3 h-3" />
            {up ? "+" : ""}{delta}% vs. Vormonat
        </span>
    );
}

function Sparkle() {
    return <span className="text-[10px]">✨</span>;
}

export default function MonthlyEarningsCard() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/vermittler/earnings/monthly")
            .then((r) => setData(r.data))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    if (loading) {
        return (
            <div className="bg-white border border-gray-200 rounded-sm p-5" data-testid="monthly-earnings-loading">
                <Loader2 className="w-5 h-5 animate-spin text-gray-400 mx-auto" />
            </div>
        );
    }
    if (!data) return null;

    return (
        <div
            className="bg-gradient-to-br from-[#111827] to-[#1f2937] text-white border-2 border-[#FFCC00] rounded-sm p-5"
            data-testid="monthly-earnings-card"
        >
            <div className="flex items-center gap-2 mb-4">
                <TrendingUp className="w-5 h-5 text-[#FFCC00]" />
                <h3 className="font-display font-black text-base tracking-tight">
                    Mein Verdienst — <span className="text-[#FFCC00]">{data.month_label}</span>
                </h3>
            </div>

            <div className="grid grid-cols-1 gap-3">
                {/* €Punkte (gesammelte Aktivitäts-Punkte) — Privat-Verdienst.
                    EUR-Provision wird jetzt im Firmen-Bereich „Meine Provisionen" gezeigt. */}
                <div className="bg-white/5 border border-white/10 rounded-sm p-4" data-testid="earnings-points">
                    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-gray-300 mb-1">
                        <Coins className="w-3.5 h-3.5 text-[#FFCC00]" />
                        €Punkte
                    </div>
                    <div className="font-display font-black text-3xl font-mono-num leading-none text-[#FFCC00]">
                        {Number(data.points || 0).toLocaleString("de-DE")}
                    </div>
                    <div className="text-[11px] text-gray-400 mt-1.5">
                        aus {data.actions} {data.actions === 1 ? "Aktion" : "Aktionen"} diesen Monat
                    </div>
                    <div className="mt-1">
                        <DeltaBadge delta={data.points_delta_pct} hasValue={data.points > 0} testid="earnings-points-delta" />
                    </div>
                </div>
            </div>

            <p className="text-[10px] text-gray-400 mt-3">
                €Punkte sammelst du über Empfehlungen &amp; Aktionen. Deine <strong>EUR-Provision</strong> als Verkäufer im Dienst findest du im Firmen-Bereich unter „Meine Provisionen".
            </p>
        </div>
    );
}
